import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import type { CuppingFormData } from '../../../hooks/inventory-hooks/useCuppingProfileForm';
import { clsx } from 'clsx';
import { NotesField, ScoreSlider } from './CuppingFormFields';
import { DEFECT_OPTIONS } from './CuppingFormConstants';
import { useLanguage } from '../../../features/language/useLanguage';

const SCAAttributeRow: React.FC<{
  control: Control<CuppingFormData>;
  name: keyof CuppingFormData;
  label: string;
  setValue: UseFormSetValue<CuppingFormData>;
  disabled?: boolean;
  testId?: string;
}> = ({ control, name, label, setValue, disabled }) => (
  <div className="flex flex-col gap-1 w-full">
    <ScoreSlider control={control} name={name} label={label} max={9} setValue={setValue} disabled={disabled} />
  </div>
);

export const SCACuppingSection: React.FC<{
  control: Control<CuppingFormData>;
  register: UseFormRegister<CuppingFormData>;
  setValue: UseFormSetValue<CuppingFormData>;
  readOnly?: boolean;
  computedScore?: number;
  enableSectionLocks?: boolean;
  isSectionLocked?: (key: string) => boolean;
  renderSectionLock?: (key: string, className?: string) => React.ReactNode;
}> = ({ control, register, setValue, readOnly, computedScore, enableSectionLocks, isSectionLocked, renderSectionLock }) => {
  const { t } = useLanguage();
  const sectionReadOnly = (key: string) => {
    if (readOnly) return true;
    if (!enableSectionLocks) return false;
    return isSectionLocked?.(key) ?? true;
  };

  return (
    <div className="space-y-px" data-test-id="cupping-sca-section">
      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 p-4 border-r border-gray-300 space-y-3">
          <SCAAttributeRow
            control={control}
            name="fragrance"
            label={t('cupping.sca.fragrance')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-fragrance-aroma')}
            testId="sca-frag"
          />
          <SCAAttributeRow
            control={control}
            name="aroma"
            label={t('cupping.sca.aroma')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-fragrance-aroma')}
            testId="sca-aroma"
          />
        </div>
        <div className="col-span-4 p-4">
          <NotesField register={register} name="notes_fragrance" label={t('reports.details.notes')} disabled={sectionReadOnly('sca-fragrance-aroma')} />
        </div>
        {renderSectionLock?.('sca-fragrance-aroma', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 p-4 border-r border-gray-300 space-y-3">
          <SCAAttributeRow
            control={control}
            name="flavor"
            label={t('cupping.formSections.labels.flavor')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-flavor-aftertaste')}
            testId="sca-flavor"
          />
          <SCAAttributeRow
            control={control}
            name="aftertaste"
            label={t('cupping.formSections.labels.aftertaste')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-flavor-aftertaste')}
            testId="sca-aftertaste"
          />
        </div>
        <div className="col-span-4 p-4">
          <NotesField register={register} name="notes_flavor" label={t('reports.details.notes')} disabled={sectionReadOnly('sca-flavor-aftertaste')} />
        </div>
        {renderSectionLock?.('sca-flavor-aftertaste', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 p-4 border-r border-gray-300">
          <SCAAttributeRow
            control={control}
            name="acidity"
            label={t('cupping.formSections.labels.acidity')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-acidity')}
            testId="sca-acidity"
          />
        </div>
        <div className="col-span-4 p-4">
          <NotesField register={register} name="notes_acidity" label={t('reports.details.notes')} disabled={sectionReadOnly('sca-acidity')} />
        </div>
        {renderSectionLock?.('sca-acidity', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 p-4 border-r border-gray-300">
          <SCAAttributeRow
            control={control}
            name="sweetness"
            label={t('cupping.formSections.labels.sweetness')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-sweetness')}
            testId="sca-sweetness"
          />
        </div>
        <div className="col-span-4 p-4">
          <NotesField register={register} name="notes_sweetness" label={t('reports.details.notes')} disabled={sectionReadOnly('sca-sweetness')} />
        </div>
        {renderSectionLock?.('sca-sweetness', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 p-4 border-r border-gray-300">
          <SCAAttributeRow
            control={control}
            name="body"
            label={t('cupping.formSections.labels.mouthfeel')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-mouthfeel')}
            testId="sca-body"
          />
        </div>
        <div className="col-span-4 p-4">
          <NotesField register={register} name="notes_mouthfeel" label={t('reports.details.notes')} disabled={sectionReadOnly('sca-mouthfeel')} />
        </div>
        {renderSectionLock?.('sca-mouthfeel', 'absolute top-3 right-3')}
      </div>

      <div className="grid grid-cols-12 border border-gray-300 bg-white relative">
        <div className="col-span-8 p-4 border-r border-gray-300">
          <SCAAttributeRow
            control={control}
            name="overall"
            label={t('cupping.formSections.labels.overall')}
            setValue={setValue}
            disabled={sectionReadOnly('sca-overall')}
            testId="sca-overall"
          />
        </div>
        <div className="col-span-4 p-4">
          <NotesField register={register} name="notes_body" label={t('reports.details.notes')} disabled={sectionReadOnly('sca-overall')} />
        </div>
        {renderSectionLock?.('sca-overall', 'absolute top-3 right-3')}
      </div>

      <div className="border border-gray-300 bg-white p-4 grid grid-cols-12 gap-4 items-stretch relative">
        <div className="col-span-8 space-y-4">
          <div>
            <div className="text-xs font-bold text-gray-700 uppercase mb-1">{t('cupping.sca.nonUniformCups')}</div>
            <Controller
              control={control}
              name="sca_non_uniform_cups"
              defaultValue={0}
              render={({ field }) => (
                <div className="flex gap-2">
                  {[0, 1, 2, 3, 4, 5].map((v) => (
                    <label key={v} className={clsx('flex items-center gap-2 text-sm', sectionReadOnly('sca-defects') && 'text-gray-900 font-medium')}>
                      <input
                        type="radio"
                        value={v}
                        checked={field.value === v}
                        onChange={() => field.onChange(v)}
                        disabled={sectionReadOnly('sca-defects')}
                        className={clsx(sectionReadOnly('sca-defects') && "disabled:opacity-100 disabled:border-gray-500 disabled:checked:bg-gray-700")}
                        data-test-id={`sca-non-uniform-${v}`}
                      />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              )}
            />
          </div>

          <div>
            <div className="text-xs font-bold text-gray-700 uppercase mb-1">{t('cupping.sca.defectiveCups')}</div>
            <Controller
              control={control}
              name="sca_defective_cups"
              defaultValue={0}
              render={({ field }) => (
                <div className="flex gap-2">
                  {[0, 1, 2, 3, 4, 5].map((v) => (
                    <label key={v} className={clsx('flex items-center gap-2 text-sm', sectionReadOnly('sca-defects') && 'text-gray-900 font-medium')}>
                      <input
                        type="radio"
                        value={v}
                        checked={field.value === v}
                        onChange={() => field.onChange(v)}
                        disabled={sectionReadOnly('sca-defects')}
                        className={clsx(sectionReadOnly('sca-defects') && "disabled:opacity-100 disabled:border-gray-500 disabled:checked:bg-gray-700")}
                        data-test-id={`sca-defective-${v}`}
                      />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              )}
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase">
              {t('cupping.sca.defectsIfAny')}
            </label>
            <div className="flex flex-wrap gap-2">
              {DEFECT_OPTIONS.map((defect) => (
                <label
                    key={defect}
                    className={clsx(
                      "flex items-center gap-2 text-sm text-red-700 cursor-pointer bg-red-50 px-2 py-1 rounded border border-red-100",
                      sectionReadOnly('sca-defects') && "cursor-not-allowed text-red-900 bg-red-100 border-red-300 font-medium"
                    )}
                  >
                  <input
                    type="checkbox"
                    value={defect}
                    {...register("defects")}
                    disabled={sectionReadOnly('sca-defects')}
                    className="h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500 disabled:opacity-100 disabled:border-red-500 disabled:checked:bg-red-800"
                  />
                  <span>{defect}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-4 flex flex-col justify-end items-end">
          <div className="p-4 bg-gray-50 rounded border border-gray-200">
            <div className="text-xs font-bold text-gray-700 uppercase mb-1">{t('cupping.sca.score')}</div>
            <input
              type="text"
              readOnly
              value={computedScore ?? ''}
              className="w-full px-3 py-2 rounded border border-gray-300 bg-gray-100 text-gray-800 font-semibold"
              data-test-id="sca-final-score"
            />
          </div>
        </div>
        {renderSectionLock?.('sca-defects', 'absolute top-3 right-3')}
      </div>
    </div>
  );
};
